import { IntegrationService } from './../../../services/integration.service';
import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CloverIntegrationComponent } from './modal/clover-integration/clover-integration.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { FNFResponse } from '../../../models/response';
import { RestaurantService } from '../../../services/restaurant.service';
import { SquareIntegrationComponent } from './modal/square-integration/square-integration.component';
import { SwirepayIntegrationComponent } from './modal/swirepay-integration/swirepay-integration.component';
import { Router } from '@angular/router';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import { isFnfLike, POS_PROVIDER, POS_PROVIDER_NAMES } from '../../../utils/pos-provider.util';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-hardware',
  standalone: true,
  imports: [],
  templateUrl: './hardware.component.html',
  styleUrl: './hardware.component.css',
})
export class HardwareComponent {
  integrationService = inject(IntegrationService);
  ngxSpinnerService = inject(NgxSpinnerService);
  restaurantService = inject(RestaurantService);
  toastr = inject(ToastrService);
  router = inject(Router);
  cloverAuthDetails: any;
  squareAuthDetails: any;
  squareOnboardDetails: any;
  squareOnboardStatus: any;
  currentRestaurant: any;
  isShowSquareCard = false;
  isShowCloverCard = false;
  isShowSwirepayCard = false;
  /** Latest swirepay check_status payload: status, account_id, shop_gid, shop_name. */
  swirepayStatus: any = null;

  get posProviderDisplayName(): string {
    const provider = this.currentRestaurant?.pos_provider;
    return POS_PROVIDER_NAMES[provider] || provider || 'your hardware provider';
  }

  get isSwirepayProvider(): boolean {
    return this.currentRestaurant?.pos_provider === POS_PROVIDER.SWIREPAY;
  }

  /** Lower-cased swirepay status, '' until check_status answers. */
  get swirepayOnboardStatus(): string {
    return (this.swirepayStatus?.status || '').toLowerCase();
  }

  /** Fork & Find, or no POS chosen yet — the activation banner and the Fork &
   * Find row's highlight apply to both. */
  get isFnfLikeProvider(): boolean {
    return isFnfLike(this.currentRestaurant?.pos_provider);
  }

  constructor(private dialog: MatDialog) {}

  ngOnInit() {
    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      this.currentRestaurant = restaurant;
    });
    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      if (!flags) {
        return;
      }
      if (!isFeatureActive(flags, FEATURE_FLAG_KEYS.HARDWARE)) {
        this.router.navigate(['/']);
        return;
      }
      this.isShowSquareCard = isFeatureActive(flags, FEATURE_FLAG_KEYS.SQUARE);
      this.isShowCloverCard = isFeatureActive(flags, FEATURE_FLAG_KEYS.CLOVER);
      this.isShowSwirepayCard = isFeatureActive(flags, FEATURE_FLAG_KEYS.SWIREPAY);
    });
    this.restaurantService.restaurantPosProvider$.subscribe((provider: any) => {
      if (provider == 'square') {
        this.getSquareStatus();
        this.getSquareOnboardDetails();
      } else if (provider == POS_PROVIDER.SWIREPAY) {
        this.getSwirepayStatus();
      }
    });
  }

  enableFnf() {
    this.ngxSpinnerService.show();
    this.restaurantService.enableForkAndFind(this.currentRestaurant).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.toastr.success('Fork & Find hardware activation request sent successfully', 'Success');
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  getCloverAuthorizationDetails() {
    this.ngxSpinnerService.show();
    this.integrationService.getCloverAuthorization().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.cloverAuthDetails = response.data;
        this.ngxSpinnerService.hide();
        this.enableClover();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  enableClover() {
    const dialogRef = this.dialog.open(CloverIntegrationComponent, {
      data: { cloverAuthDetails: this.cloverAuthDetails },
      width: '450px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result !== '') {
        if (result === 'finishSetup') {
          this.currentRestaurant.pos_provider = 'clover';
          this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
          this.restaurantService.restaurantPosProvider$.next('clover');
        }
      }
    });
  }

  getSquareAuthorizationDetails() {
    this.ngxSpinnerService.show();
    this.integrationService.getSquareAuthorization().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.squareAuthDetails = response.data;
        this.ngxSpinnerService.hide();
        this.enableSquare();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  enableSquare() {
    const dialogRef = this.dialog.open(SquareIntegrationComponent, {
      data: { squareAuthDetails: this.squareAuthDetails },
      width: '450px',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result !== '') {
        if (result === 'finishSetup') {
          this.currentRestaurant.pos_provider = 'square';
          this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
          this.restaurantService.restaurantPosProvider$.next('square');
        }
      }
    });
  }

  getSquareStatus() {
    this.integrationService.getSquareStatus().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          console.log('No data found!', 'Error');
          return;
        }
        this.squareOnboardStatus = response.data?.status?.toLowerCase();
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  getSquareOnboardDetails() {
    this.integrationService.getSquareOnbiardDetails().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          console.log('No data found!', 'Error');
          return;
        }
        this.squareOnboardDetails = response.data;
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  get squareLocationName(): string {
    return this.squareOnboardDetails?.location?.name || '';
  }

  get squareMenuNames(): string {
    return (this.squareOnboardDetails?.selected_menus || [])
      .map((menu: any) => menu.name)
      .join(', ');
  }

  disconnectConfirmation() {
    this.confirmDisconnect('Square', () => this.disconnectSquareConnection());
  }

  disconnectSwirepayConfirmation() {
    this.confirmDisconnect('Swirepay', () => this.disconnectSwirepayConnection());
  }

  /** Shared "Are you sure?" dialog for the Disconnect buttons. */
  private confirmDisconnect(providerName: string, onConfirm: () => void) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: `Are you sure you want to disconnect ${providerName}?` },
      width: '405px',
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result !== '') {
        if (result) {
          onConfirm();
        }
      }
    });
  }

  disconnectSquareConnection() {
    this.ngxSpinnerService.show();
    this.integrationService.disconnectSquare().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.squareOnboardStatus = 'revoked';
        this.squareOnboardDetails = null;
        this.toastr.success('Square disconnected successfully', 'Success');
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  enableSwirepay() {
    const dialogRef = this.dialog.open(SwirepayIntegrationComponent, {
      // Reconnect pre-fills the dialog with the ids already on file.
      data: { swirepayStatus: this.swirepayStatus },
      width: '450px',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result === 'finishSetup') {
        // swirepay-service already set pos_provider on the restaurant when the
        // sync completed; mirror it locally so every subscriber re-renders.
        this.currentRestaurant.pos_provider = POS_PROVIDER.SWIREPAY;
        this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
        this.restaurantService.restaurantPosProvider$.next(POS_PROVIDER.SWIREPAY);
      }
    });
  }

  getSwirepayStatus() {
    this.integrationService.getSwirepayStatus().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response?.data) {
          console.log(response?.error?.error?.message || response?.error?.message, 'Error');
          return;
        }
        this.swirepayStatus = response.data;
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  disconnectSwirepayConnection() {
    this.ngxSpinnerService.show();
    this.integrationService.swirepayDisconnect().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        // swirepay-service puts the restaurant back on Fork & Find.
        this.swirepayStatus = null;
        if (this.currentRestaurant) {
          this.currentRestaurant.pos_provider = POS_PROVIDER.FNF;
          this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
        }
        this.restaurantService.restaurantPosProvider$.next(POS_PROVIDER.FNF);
        this.toastr.success('Swirepay disconnected successfully', 'Success');
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }
}
